import { NgIf } from '@angular/common';
import { Component, ElementRef, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TenantsAuthApi } from '@buspro/web/apis';
import {
  ErrorDisplayComponent,
  ExtendedProblemDetail,
  syncAutofilledInputs,
  useFormErrorHandler,
} from '@buspro/web/core';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzResultComponent } from 'ng-zorro-antd/result';
import { NGXLogger } from 'ngx-logger';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-set-new-password-form',
  templateUrl: './set-new-password-form.component.html',
  standalone: true,
  providers: [NzNotificationService],
  imports: [NgIf, ReactiveFormsModule, NzInputDirective, RouterLink, NzResultComponent, ErrorDisplayComponent],
  styleUrls: ['./set-new-password-form.component.scss'],
})
export class SetNewPasswordFormComponent {
  private fb = inject(FormBuilder);
  private notification = inject(NzNotificationService);
  private route = inject(ActivatedRoute);
  private tenantsAuthApi = inject(TenantsAuthApi);
  private logger = inject(NGXLogger);
  private formErrorHandler = useFormErrorHandler();

  form = this.fb.group({
    password: [null, [Validators.required]],
    password_confirm: [null, [Validators.required]],
  });

  private setPasswordFormRef = viewChild<ElementRef<HTMLFormElement>>('setPasswordForm');

  submitted = false;
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  isPasswordVisible = signal(false);
  isPasswordConfirmVisible = signal(false);

  // Reset password mutation using TanStack Query
  resetPasswordMutation = injectMutation(() => ({
    mutationFn: (payload: { password: string; confirmPassword: string; token: string }) => {
      return lastValueFrom(
        this.tenantsAuthApi.tenantsAuthControllerResetPassword({
          body: payload,
        }),
      );
    },
    onSuccess: () => {
      this.logger.log('Password reset completed successfully');
      this.clearErrors();
      this.submitted = true;
      this.notification.success('✅ Password Changed', 'You can now log in with your new password');
    },
    onError: (error: unknown) => {
      this.logger.error('Password reset failed:', error);

      const result = this.formErrorHandler.handleFormError(error, {
        formName: 'reset-password-form',
      });

      this.showError.set(true);
      this.errorDetails = result.problemDetail;
    },
  }));

  login() {
    if (this.resetPasswordMutation.isPending()) return;

    // Clear previous errors
    this.clearErrors();

    // Adopt anything a password manager filled in — assigning input.value
    // never reaches the control, so the guard below would reject a form whose
    // fields are visibly complete.
    syncAutofilledInputs(this.form, this.setPasswordFormRef()?.nativeElement);

    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      this.notification.warning('Invalid Form', 'Please provide your password');
      return;
    }

    if (this.form.get('password')?.value !== this.form.get('password_confirm')?.value) {
      this.notification.error('Passwords do not match', 'Please check your inputs');
      return;
    }

    const { token } = this.route.snapshot.queryParams;
    if (!token) {
      this.notification.error('Invalid token', '');
      return;
    }

    this.resetPasswordMutation.mutate({
      password: this.form.get('password')!.value!,
      confirmPassword: this.form.get('password_confirm')!.value!,
      token,
    });
  }

  onRetry() {
    this.clearErrors();
    this.login();
  }

  onDismissError() {
    this.clearErrors();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  // Getter for backward compatibility with template
  get isSubmitting(): boolean {
    return this.resetPasswordMutation.isPending();
  }
}
